import { NgSignaturePadOptions, SignaturePadComponent } from '@almothafar/angular-signature-pad';
import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { SignatureFieldModel, SignatureFieldProps } from '@shared/components/data-form/data-form.model';
import { DataFormService } from '@shared/components/data-form/data-form.service';
import { Role } from '@shared/enums/roles.enum';
import { UserProfile } from '@shared/models/domain/domain.model';
import { SafePipe } from '@shared/pipes/safe.pipe';
import { WebsiteSettingsViewService } from '@shared/services/settings.service';

@Component({
    selector: 'formly-field-custom-signature',
    templateUrl: './signature.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./signature.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatSlideToggle, FormsModule, ReactiveFormsModule, MatIconButton, NgClass, MatIcon, MatFormField, MatInput, SignaturePadComponent, MatLabel, SafePipe]
})
export class SignatureComponent extends FieldType<FieldTypeConfig<SignatureFieldProps>> implements AfterViewInit, OnInit, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private router = inject(Router);
    private authService = inject(AuthService);
    private websiteSettings = inject(WebsiteSettingsViewService);
    private dataFormService = inject(DataFormService);

    private _resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    private currentUserProfile: UserProfile;
    isOffice = false;
    isCustomerRole = false;
    userNameFormControl = new FormControl<string>('');
    signatureDate = new Date().toISOString().slice(0, 10);
    dateFormControl = new FormControl<string>(this.signatureDate);
    description: string;
    drawSignatureControl = new FormControl<boolean>(true);

    @ViewChild('sigContainer') sigContainer: ElementRef<HTMLDivElement>;
    @ViewChild('detailsPad') detailsPad: ElementRef<HTMLCanvasElement>;
    @ViewChild('signature')
    public signaturePad: SignaturePadComponent;

    signaturePadOptions: NgSignaturePadOptions = {
        minWidth: 5,
        canvasWidth: 500,
        canvasHeight: 300,
        backgroundColor: '#fff',
        penColor: 'black'
    };

    constructor() {
        super();
        this.isOffice = this.router.url?.toLowerCase().includes('/office');
        this.isCustomerRole = this.authService?.userProfile?.userRoles?.some(role => role.roleName === Role.Customer);

        if (this.field?.props?.descriptionSlug) {
            this.websiteSettings.currentWebsiteSettings$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((settings) => {
                    if (settings) {
                        this.description = settings[this.field.props.descriptionSlug].settingsValue;
                    }
                });
        }

        if (this.isOffice) {
            this.userNameFormControl.enable();
        } else {
            this.userNameFormControl.disable();
        }
        this.dateFormControl.disable();
        this.dataFormService.signatureUser$.asObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (event) {
                    this.userNameFormControl.setValue(event);
                }
            });
        
        // Subscribe to the drawSignatureControl value changes to generate the readonly signature
        this.drawSignatureControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((enabled) => {
            if (enabled) {
                if (this.signaturePad) this.signaturePad.on();
                delete (this.field.model as SignatureFieldModel)['defaultSignatureMediaId'];
            }
            delete (this.field.model as SignatureFieldModel)['withoutSignOnSignBySignature'];
            this.resizeCanvas();
        });

        // Subscribe to the userNameFormControl value changes to generate the readonly signature
        this.userNameFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (!this.drawSignatureControl.value) {
                this.generateReadonlySignature();
            }
        });
    }

    /**
     * This function is used to generate a readonly signature URL based on the user's name.
     */
    generateReadonlySignature(): void {
        const canvas = this.detailsPad?.nativeElement;
        canvas.width = this.sigContainer?.nativeElement?.clientWidth > 0 ? this.sigContainer.nativeElement.clientWidth : this.signaturePadOptions?.canvasWidth;
        canvas.height = (this.sigContainer?.nativeElement?.clientHeight > 0 ? this.sigContainer.nativeElement.clientHeight : this.signaturePadOptions?.canvasHeight) + 60;
        const context = canvas.getContext('2d');
        const userName = this.userNameFormControl?.value;

        const fontSizes = { sm: 28, md: 40, lg: 20, xl: 40 };
        const positions = { sm: 5, md: 15, lg: 5, xl: 15 };

        const screenWidth = window.innerWidth;
        const sizeKey = screenWidth < 425 ? 'sm' : screenWidth <= 959 ? 'md' : screenWidth <= 1400 ? 'lg' : 'xl';
        const fontSize = fontSizes[sizeKey];
        const xPosition = positions[sizeKey];

        this.currentUserProfile = this.authService?.userProfile?.userProfile;
        document.fonts.load(`${fontSize}px "Dancing Script"`).then(() => {
            const drawText = () => {
                context.fillStyle = 'white';
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.font = `italic ${fontSize}px "Dancing Script", cursive`;
                context.fillStyle = 'black';
                context.fillText(userName, xPosition, 180, canvas.width);
            };
            if (context && (!this.currentUserProfile?.signatureMediaId || (this.field.model as SignatureFieldModel)?.uploadSignature)) {
                drawText();
                this.signaturePad.fromDataURL(canvas.toDataURL());
                this.formControl?.setValue(canvas.toDataURL());
                (this.field.model as SignatureFieldModel)['withoutSignOnSignBySignature'] = canvas.toDataURL();
                this.signaturePad.off();
                
                if (!(this.field?.model as SignatureFieldModel)?.uploadSignature) {
                    // Draw additional text for "Signed By" and "Signed On"
                    context.font = '20px Arial';
                    context.fillText(`Signed By: ${userName}`, 0, 30, canvas.width);
                    context.fillText(`Signed On: ${this.dateFormControl.value}`, 0, 60, canvas.width);
                    
                    // Set the formControl signature with "Signed By" and "Signed On"
                    this.formControl?.setValue(canvas.toDataURL());
                    // Clear and redraw signature
                    drawText();
                    this.signaturePad.fromDataURL(canvas.toDataURL());
                }
            } else if (this.currentUserProfile?.signatureMediaId) {
                this.signaturePad.off();
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    // Draw the image onto the canvas
                    context.fillStyle = 'white';
                    context.fillRect(0, 0, canvas.width, canvas.height);
                    context.drawImage(img, 0, 60, canvas.width, canvas.height - 60);
                    this.signaturePad.fromDataURL(canvas.toDataURL());

                    // Add "Signed By" and "Signed On" text
                    context.font = '20px Arial';
                    context.fillStyle = 'black';
                    context.fillText(`Signed By: ${userName}`, 0, 30, canvas.width);
                    context.fillText(`Signed On: ${this.dateFormControl.value}`, 0, 60, canvas.width);
                    this.formControl.setValue(canvas.toDataURL());
                    (this.field.model as SignatureFieldModel)['defaultSignatureMediaId'] = this.currentUserProfile?.signatureMediaId;
                };
                img.src = this.authService.userProfile?.signatureMediaUrl;
            }
        });
    }

    ngOnInit(): void {
        if (this.isCustomerRole) this.drawSignatureControl.setValue(false);
        if ((this.field.model as SignatureFieldModel)?.onlyDrawSignature) this.drawSignatureControl.setValue(true);
    }

    ngOnDestroy(): void {
        if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    }

    @HostListener('window:resize')
    onResize() {
        this.resizeCanvas();
    }

    ngAfterViewInit(): void {
        this.resizeCanvas();
        if (this.field?.props?.descriptionSlug) {
            this.websiteSettings.currentWebsiteSettings$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((settings) => {
                    if (settings) {
                        this.description = settings[this.field.props.descriptionSlug].settingsValue;
                    }
                });
        }
    }

    drawComplete() {
        const canvas = this.detailsPad.nativeElement;
        // The pad is sized to its container (see resizeCanvas); v10 no longer writes that size back to signaturePadOptions.
        const container = this.sigContainer.nativeElement;
        canvas.width = (container.clientWidth > 0 ? container.clientWidth : this.signaturePadOptions.canvasWidth) - 100;
        canvas.height = (container.clientHeight > 0 ? container.clientHeight : this.signaturePadOptions.canvasHeight) + 60;
        const context = canvas.getContext('2d');
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(this.signaturePad.getCanvas(), 0, 60, canvas.width, canvas.height - 60);
        (this.field.model as SignatureFieldModel)['withoutSignOnSignBySignature'] = canvas.toDataURL();
        if (!(this.field?.model as SignatureFieldModel)?.uploadSignature) {
            context.font = '20px Arial';
            context.fillStyle = 'black';
            context.fillText(`Signed By: ${this.userNameFormControl.value}`, 0, 30, this.sigContainer.nativeElement.clientWidth);
            context.fillText(`Signed On: ${this.dateFormControl.value}`, 0, 60, this.sigContainer.nativeElement.clientWidth);
        }
        const value = canvas.toDataURL();
        (this.field.model as Record<string, string>)[this.field.key.toString()] = value;
        this.formControl.setValue(value);
    }

    resizeCanvas(): void {
        if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
        this._resizeTimeout = setTimeout(() => {
            if (!this.signaturePad || !this.sigContainer?.nativeElement) {
                return;
            }

            this.resetImage();
            this.signaturePad.set('canvasWidth', this.sigContainer.nativeElement.clientWidth > 0 ? this.sigContainer.nativeElement.clientWidth : this.signaturePadOptions.canvasWidth);
            this.signaturePad.set('canvasHeight', this.sigContainer.nativeElement.clientHeight > 0 ? this.sigContainer.nativeElement.clientHeight : this.signaturePadOptions.canvasHeight);
            if (!this.drawSignatureControl.value) this.generateReadonlySignature();
            this.changeDetectorRef.detectChanges();
        }, 500);
    }

    resetImage(): void {
        this.signaturePad.clear();
        this.formControl.setValue(null);
        (this.field.model as Record<string, string>)[this.field.key.toString()] = null;
        this.changeDetectorRef.detectChanges();
    }
}